import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { createComponentLogger } from '../../utils/logger';
import { getErrorMessage } from '../../utils/errorHandler';
import { isCleverEnabled, getCleverLoginUrl } from '../../services/rosteringService';
import { useLanguage } from '../../contexts/LanguageContext';
import './Auth.css';
import { friendlyAuthMessage } from '../../utils/authErrorMessage';
import { keepSignedIn, setKeepSignedIn } from '../../config/firebase';

const logger = createComponentLogger('Login');

// Translated 2026-09-05 against the professionally translated dictionaries in
// config/i18nConfig.ts. Only strings with an existing key are keyed: the rest
// keep their English text rather than being machine-translated into six
// languages, because a district product's translations get certified and
// unreviewed strings in front of children are not an improvement on English.
export const Login: React.FC = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  // S223: unticked by default — on a shared classroom device, closing the tab signs out.
  const [rememberDevice, setRememberDevice] = useState(keepSignedIn);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle, loginWithMicrosoft } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    let isValid = true;
    
    // Clear previous errors
    setEmailError('');
    setPasswordError('');
    setError('');
    
    // Validate email
    if (!email) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }
    
    // Validate password
    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    }
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form first
    if (!validateForm()) {
      if (!email && !password) {
        setError('Please enter your email and password');
      } else if (!email) {
        setError('Please enter your email address');
      } else if (!password) {
        setError('Please enter your password');
      }
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      await login(email, password);
      await setKeepSignedIn(rememberDevice).catch((err) => logger.warn('Could not set sign-in persistence', err));
      navigate('/dashboard');
    } catch (error: unknown) {
      logger.error('Login error:', error);
      
      // Never render the SDK's own message: a wrong password throws a FirebaseError
      // whose text is "Firebase: Error (auth/invalid-credential).", which names the
      // backend and tells a child nothing they can act on.
      setError(friendlyAuthMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // #46 fix (2026-05-18): if the user is new, route to a ToS-acceptance page
  // before /dashboard. The user doc was created with `termsAcceptedAt: null`
  // by AuthContext; the accept-terms route captures consent and stamps the
  // timestamp before letting them in. Shared across federated providers.
  const handleFederatedLogin = async (
    provider: 'google' | 'microsoft',
    signIn: () => Promise<{ isNewUser: boolean } | void>
  ) => {
    try {
      setError('');
      setLoading(true);
      const result = await signIn();
      // After, not before: anything awaited ahead of the popup can get it blocked.
      await setKeepSignedIn(rememberDevice).catch((err) => logger.warn('Could not set sign-in persistence', err));

      // S98: this was `if (isNewUser) → /accept-terms; else → /dashboard`, so an
      // UNKNOWN result took the permissive branch — and `/accept-terms` is the only
      // place the OAuth path collects a date of birth. A brand-new federated
      // account whose flag did not survive skipped it entirely, leaving an account
      // with no age on record that `requiresConsent()` reads as not COPPA-covered.
      // The same fix as `Register.handleGoogleSignup`: only an explicit
      // `isNewUser: false` goes straight in, and /accept-terms is idempotent for an
      // account that already has terms and an age.
      const knownReturningUser =
        result !== null &&
        typeof result === 'object' &&
        'isNewUser' in result &&
        result.isNewUser === false;

      if (knownReturningUser) {
        navigate('/dashboard');
      } else {
        navigate(`/accept-terms?from=${provider}`);
      }
    } catch (error: unknown) {
      logger.error(`${provider} login error:`, error);
      // Same leak as the password path: a federated failure throws a FirebaseError whose
      // message names the SDK. `friendlyAuthMessage` covers the popup codes too.
      const label = provider === 'google' ? 'Google' : 'Microsoft';
      const friendly = friendlyAuthMessage(error);
      setError(
        friendly === 'Something went wrong signing you in. Please try again.'
          ? `Could not sign in with ${label}. Please try again.`
          : friendly
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => handleFederatedLogin('google', loginWithGoogle);
  const handleMicrosoftLogin = () => handleFederatedLogin('microsoft', loginWithMicrosoft);

  // S153: `getCleverLoginUrl` throws on purpose when the deployment has no
  // client id (S82), and `secureToken` inside it throws rather than degrading to
  // Math.random(). Both were called bare inside the click handler, and React
  // renders nothing for an exception thrown from an event handler — so the
  // button did nothing at all and gave no reason. Rule 7: an operation that
  // cannot do its job must say so.
  const handleCleverLogin = () => {
    try {
      setError('');
      window.location.href = getCleverLoginUrl();
    } catch (err: unknown) {
      logger.error('Clever login error:', err);
      setError(
        getErrorMessage(err) ||
          'Clever sign-in is not available right now. Please use another way to sign in.'
      );
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">Welcome Back</h1>
        <p className="auth-subtitle">Sign in to continue to NS Gaming Studio</p>
        
        {/* S152: every field error on this screen carried role="alert" and the
            form-level one did not, so "Failed to log in" was announced to
            nobody. A blind student typing the wrong password got silence. */}
        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t('form.email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
                if (error) setError('');
              }}
              required
              placeholder="Enter your email"
              className={emailError ? 'error' : ''}
              aria-describedby={emailError ? "email-error" : undefined}
              aria-invalid={!!emailError}
            />
            {emailError && (
              <div id="email-error" className="field-error" role="alert">
                {emailError}
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('form.password')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError('');
                if (error) setError('');
              }}
              required
              placeholder="Enter your password"
              className={passwordError ? 'error' : ''}
              aria-describedby={passwordError ? "password-error" : undefined}
              aria-invalid={!!passwordError}
            />
            {passwordError && (
              <div id="password-error" className="field-error" role="alert">
                {passwordError}
              </div>
            )}
          </div>
          
          <div className="form-group remember-device">
            <label htmlFor="remember-device">
              <input
                id="remember-device"
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
              />{' '}
              Keep me signed in on this device
            </label>
            <div className="field-hint">Leave this unticked on a shared or school computer.</div>
          </div>

          <div className="form-actions">
            <Link to="/forgot-password" className="forgot-link">
              Forgot Password?
            </Link>
          </div>
          
          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <div className="auth-divider">
          <span>OR</span>
        </div>
        
        <button 
          onClick={handleGoogleLogin} 
          className="auth-button google-button"
          disabled={loading}
        >
          <svg className="google-icon" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <button
          onClick={handleMicrosoftLogin}
          className="auth-button microsoft-button"
          disabled={loading}
        >
          <svg className="microsoft-icon" viewBox="0 0 23 23" aria-hidden="true">
            <path fill="#f25022" d="M1 1h10v10H1z"/>
            <path fill="#7fba00" d="M12 1h10v10H12z"/>
            <path fill="#00a4ef" d="M1 12h10v10H1z"/>
            <path fill="#ffb900" d="M12 12h10v10H12z"/>
          </svg>
          Continue with Microsoft
        </button>

        {isCleverEnabled() && (
          <button
            type="button"
            onClick={handleCleverLogin}
            className="auth-button clever-button"
            disabled={loading}
          >
            Log in with Clever
          </button>
        )}

        <div className="auth-footer">
          <p>Don't have an account? <Link to="/register">Sign up</Link></p>
        </div>
      </div>
    </div>
  );
};